import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { Button, Field, Notice } from "../components";
import { Link } from "../router";
import { SEX_LABEL } from "./patientLabels";

const fmt = (iso: string) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
interface Side { id: string; uhid: string; fullName: string; sex: string; age: string; mobile: string | null }
interface Item { id: string; status: string; reason: string; requestedAt: string; requestedByMe: boolean; decisionNote: string | null; source: Side | null; target: Side | null }

export function Duplicates() {
  const [tab, setTab] = useState<"pending" | "approved">("pending");
  const [items, setItems] = useState<Item[] | null>(null);
  const [canApprove, setCanApprove] = useState(false);
  const [msg, setMsg] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const load = useCallback(() => {
    api.authed(`/api/v1/patient-merges?status=${tab}`).then((r) => { setItems(r.items); setCanApprove(r.canApprove); }).catch((e) => setMsg({ tone: "error", text: e.message }));
  }, [tab]);
  useEffect(load, [load]);

  async function act(id: string, action: "approve" | "reject" | "revert", note: string) {
    setMsg(null);
    try {
      await api.authed(`/api/v1/patient-merges/${id}/${action}`, { body: { note } });
      setMsg({ tone: "success", text: action === "approve" ? "Records merged. The old UHID now opens the kept record." : action === "reject" ? "Request rejected." : "Merge undone. Both records are active again." });
      load();
    } catch (ex) { setMsg({ tone: "error", text: (ex as ApiError).message }); }
  }

  return (
    <>
      <h1>Duplicate records</h1>
      <p className="lede">Merging needs two people: one reports the duplicate, another checks and approves it.</p>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={tab === "pending"} onClick={() => setTab("pending")}>Waiting for approval</button>
        <button role="tab" aria-selected={tab === "approved"} onClick={() => setTab("approved")}>Merged</button>
      </div>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {items && items.length === 0 && <p className="empty">{tab === "pending" ? "Nothing waiting. Duplicates are reported from a patient's page." : "No merged records yet."}</p>}
      <ul className="merge-list">{items?.map((m) => <MergeCard key={m.id} m={m} canApprove={canApprove} onAct={act} />)}</ul>
    </>
  );
}

function SideCard({ s, label }: { s: Side | null; label: string }) {
  if (!s) return <div className="side"><span className="muted">{label}: record not found</span></div>;
  return (
    <div className="side">
      <span className="side-label">{label}</span>
      <Link to={`/app/patients/${s.id}`}>{s.fullName}</Link>
      <span className="mono">{s.uhid}</span>
      <span className="muted">{s.age}, {SEX_LABEL[s.sex]}{s.mobile ? `, ${s.mobile}` : ""}</span>
    </div>
  );
}

function MergeCard({ m, canApprove, onAct }: { m: Item; canApprove: boolean; onAct: (id: string, a: "approve" | "reject" | "revert", note: string) => void }) {
  const [note, setNote] = useState("");
  return (
    <li className="merge-card">
      <div className="pair"><SideCard s={m.source} label="Duplicate (will be merged)" /><span className="arrow" aria-hidden="true">→</span><SideCard s={m.target} label="Kept" /></div>
      <p><span className="muted">Reason:</span> {m.reason}</p>
      <p className="muted">Reported {fmt(m.requestedAt)}{m.decisionNote ? `. Note: ${m.decisionNote}` : ""}</p>
      {m.status === "pending" && (m.requestedByMe
        ? <p className="tag-wait">You reported this. Another person needs to approve it.</p>
        : canApprove && <div className="decide">
            <Field label="Note" optional value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="form-actions">
              <Button type="button" onClick={() => onAct(m.id, "approve", note)}>Approve merge</Button>
              <Button type="button" variant="quiet" disabled={note.trim().length < 5} title="Add a note to reject" onClick={() => onAct(m.id, "reject", note)}>Reject</Button>
            </div>
          </div>)}
      {m.status === "approved" && canApprove && <Button type="button" variant="quiet" onClick={() => onAct(m.id, "revert", "")}>Undo merge</Button>}
    </li>
  );
}
